import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, KeyRound, Plus, Trash2 } from 'lucide-react';
import {
  type ActionMeta,
  type CreatedInboundToken,
  type CreateTokenRequest,
  type InboundTokenRow,
  createInboundToken,
  deleteInboundToken,
  listInboundActions,
  listInboundTokens,
} from '@/api/inboundWebhooks';
import { errorDetail } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

export function InboundWebhooksPage() {
  const { t, i18n } = useTranslation();
  const [tokens, setTokens] = useState<InboundTokenRow[]>([]);
  const [actions, setActions] = useState<ActionMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [created, setCreated] = useState<CreatedInboundToken | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listInboundTokens()
      .then(setTokens)
      .catch((err) => setError(errorDetail(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    listInboundActions()
      .then(setActions)
      .catch(() => setActions([]));
  }, [load]);

  async function handleDelete(id: string) {
    if (!confirm(t('inboundWebhooks.revokeConfirm'))) return;
    try {
      await deleteInboundToken(id);
      load();
    } catch (err) {
      setError(errorDetail(err));
    }
  }

  function handleCreated(token: CreatedInboundToken) {
    setFormOpen(false);
    setCreated(token);
    load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            {t('inboundWebhooks.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
            {t('inboundWebhooks.subtitle')}
          </p>
        </div>
        <Button size="sm" onClick={() => setFormOpen(true)} data-testid="inbound-token-new">
          <Plus className="mr-1.5 h-4 w-4" />
          {t('inboundWebhooks.create')}
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>
      )}

      {created && <TokenBanner created={created} onDismiss={() => setCreated(null)} />}

      {formOpen && (
        <CreateTokenForm
          actions={actions}
          onCreated={handleCreated}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {actions.length > 0 && (
        <Card tier="sunken" className="p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('inboundWebhooks.actions.title')}
          </h3>
          <ul className="space-y-1">
            {actions.map((a) => (
              <li key={a.name} className="flex flex-wrap items-baseline gap-2 text-xs">
                <code className="font-mono text-[var(--color-lop-text-primary)]">{a.name}</code>
                <span className="rounded bg-[var(--color-lop-bg-card-hi)] px-1.5 py-0.5 text-[10px] text-[var(--color-lop-text-secondary)]">
                  {a.requiredScope}
                </span>
                <span className="text-muted-foreground">{a.description}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : tokens.length === 0 && !formOpen ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('inboundWebhooks.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {tokens.map((tk) => (
            <Card key={tk.id} className="flex items-start gap-4 p-4" data-testid="inbound-token-row">
              <Inbox className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[var(--color-lop-text-primary)]">{tk.name}</span>
                  {tk.revoked ? (
                    <span className="rounded bg-red-900/40 px-1.5 py-0.5 text-[10px] font-semibold text-red-300">
                      {t('inboundWebhooks.revoked')}
                    </span>
                  ) : (
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                        tk.enabled
                          ? 'bg-green-900/40 text-green-300'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {tk.enabled ? t('inboundWebhooks.enabled') : t('inboundWebhooks.disabled')}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    {t('inboundWebhooks.tokenPrefix')}:{' '}
                    <code className="font-mono text-[var(--color-lop-text-secondary)]">
                      {tk.tokenPrefix}…
                    </code>
                  </span>
                  {tk.allowedScopes.length > 0 && (
                    <span>
                      {t('inboundWebhooks.scopes')}:{' '}
                      <code className="font-mono">{tk.allowedScopes.join(' ')}</code>
                    </span>
                  )}
                  <span>
                    {t('inboundWebhooks.useCount')}: {tk.useCount}
                  </span>
                  {tk.lastUsedAt && (
                    <span>
                      {t('inboundWebhooks.lastUsedAt')}:{' '}
                      {new Date(tk.lastUsedAt).toLocaleString(i18n.language)}
                    </span>
                  )}
                  {tk.expiresAt && (
                    <span>
                      {t('inboundWebhooks.expiresAt')}:{' '}
                      {new Date(tk.expiresAt).toLocaleDateString(i18n.language)}
                    </span>
                  )}
                </div>
              </div>
              {!tk.revoked && (
                <Button
                  size="sm"
                  variant="ghost"
                  title={t('inboundWebhooks.revoke')}
                  onClick={() => handleDelete(tk.id)}
                  data-testid="inbound-token-revoke"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TokenBanner({
  created,
  onDismiss,
}: {
  created: CreatedInboundToken;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const sampleCurl = `curl -X POST ${window.location.origin}/webhooks/in/${created.token}/{action}`;

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the field stays selectable */
    }
  }

  return (
    <Card tier="raised" className="border-amber-500/40 bg-amber-500/5" data-testid="inbound-token-banner">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-amber-300">{t('inboundWebhooks.token.title')}</h3>
      </div>
      <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
        {t('inboundWebhooks.token.warning')}
      </p>
      <div className="mt-3 flex gap-2">
        <Input
          value={created.token}
          readOnly
          onFocus={(e) => e.currentTarget.select()}
          className="font-mono"
        />
        <Button size="sm" onClick={() => copy(created.token)} data-testid="inbound-token-copy">
          {copied ? t('common.copied') : t('common.copy')}
        </Button>
      </div>
      <div className="mt-3">
        <label className="mb-1 block text-[11px] text-muted-foreground">
          {t('inboundWebhooks.token.sampleCurl')}
        </label>
        <pre className="overflow-x-auto rounded-md bg-background px-3 py-2 font-mono text-[11px] text-[var(--color-lop-text-secondary)]">
          {sampleCurl}
        </pre>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" variant="outline" onClick={onDismiss}>
          {t('common.close')}
        </Button>
      </div>
    </Card>
  );
}

function CreateTokenForm({
  actions,
  onCreated,
  onCancel,
}: {
  actions: ActionMeta[];
  onCreated: (t: CreatedInboundToken) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The selectable scopes come from the distinct requiredScope values of the
  // action catalogue; an admin who hasn't loaded actions can still type none.
  const availableScopes = Array.from(new Set(actions.map((a) => a.requiredScope)));

  function toggleScope(scope: string) {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const req: CreateTokenRequest = {
      name: name.trim(),
      allowedScopes: scopes,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    };
    try {
      onCreated(await createInboundToken(req));
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="mb-4 font-medium">{t('inboundWebhooks.createModal.title')}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium">{t('inboundWebhooks.name')}</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">{t('inboundWebhooks.scopes')}</label>
          {availableScopes.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {availableScopes.map((scope) => (
                <button
                  key={scope}
                  type="button"
                  onClick={() => toggleScope(scope)}
                  className={cn(
                    'rounded-full px-3 py-1 font-mono text-xs transition-colors',
                    scopes.includes(scope)
                      ? 'bg-[var(--color-lop-teal)] text-[var(--color-lop-bg-deep)]'
                      : 'bg-[var(--color-lop-bg-card-hi)] text-[var(--color-lop-text-secondary)] hover:text-[var(--color-lop-teal)]',
                  )}
                >
                  {scope}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">{t('inboundWebhooks.noScopes')}</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            {t('inboundWebhooks.expiresAt')} ({t('common.optional')})
          </label>
          <Input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="max-w-[200px]"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={saving || !name.trim()} data-testid="inbound-token-create">
            {saving ? t('common.saving') : t('inboundWebhooks.createModal.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
